// pages/PendingLoanInterested.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  AccountBalance,
  AttachMoney,
  Home,
  Layers,
} from "@mui/icons-material";
import "./PendingLoanInterested.css";

// ✅ Formatter for amounts
const formatAmount = (num) => {
  if (!num || isNaN(num)) return "";
  const n = Number(num);
  if (n >= 10000000) return `${Math.floor(n / 10000000)}Cr`;
  else if (n >= 100000) return `${Math.floor(n / 100000)}L`;
  else if (n >= 1000) return `${Math.floor(n / 1000)}K`;
  else return n.toLocaleString("en-IN");
};

// ✅ Summary generator
const getSummary = (leads) => {
  const summary = {
    PL: { count: 0, total: 0 },
    BL: { count: 0, total: 0 },
    AL: { count: 0, total: 0 },
    HL: { count: 0, total: 0 },
    LAP: { count: 0, total: 0 },
  };

  leads.forEach((lead) => {
    const type = lead.loan_type?.toUpperCase();
    if (summary[type]) {
      summary[type].count += 1;
      summary[type].total += Number(lead.req_loan_amt || 0);
    }
  });

  return summary;
};

// ✅ Loan type configuration
const loanTypeConfig = {
  PL: {
    label: "Personal Loan",
    icon: <TrendingUp fontSize="large" />,
    color: "#4CAF50",
  },
  BL: {
    label: "Business Loan",
    icon: <AccountBalance fontSize="large" />,
    color: "#2196F3",
  },
  AL: {
    label: "Auto Loan",
    icon: <AttachMoney fontSize="large" />,
    color: "#FF9800",
  },
  HL: {
    label: "Home Loan",
    icon: <Home fontSize="large" />,
    color: "#9C27B0",
  },
  LAP: {
    label: "Loan Against Property",
    icon: <Layers fontSize="large" />,
    color: "#F44336",
  },
};

// ✅ Summary component (NEW DESIGN)
const LoanSummary = ({ summary }) => (
  <Box sx={{ mb: 3 }}>
    <Grid container spacing={2} justifyContent="center">
      {Object.entries(summary).map(([type, data]) => {
        const config = loanTypeConfig[type] || {};
        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={type}>
            <Card
              elevation={4}
              sx={{
                borderRadius: 3,
                background: `linear-gradient(135deg, ${config.color}20, #fff)`,
                textAlign: "center",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0px 6px 16px rgba(0,0,0,0.15)",
                },
              }}
            >
              <CardContent>
                {/* Icon */}
                <Box sx={{ color: config.color, mb: 1 }}>{config.icon}</Box>

                {/* Loan Name */}
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: "#333" }}
                >
                  {config.label || type}
                </Typography>
{/* Leads & Total Side by Side with small gap */}
<Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1 }}>
  <Typography variant="body2" sx={{ color: "#555" }}>
    Leads: <b>{data.count}</b>
  </Typography>
  <Typography variant="body2" sx={{ color: "#222" }}>
    Total: <b>{formatAmount(data.total)}</b>
  </Typography>
</Box>

              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>

    {/* Heading moved BELOW */}
    <Typography
      variant="h6"
      fontWeight="bold"
      align="center"
      sx={{ mt: 4, color: "#333" }}
      className="pli-heading"
    >
      📝 Pending Loan Interested Leads
    </Typography>
  </Box>
);

const PendingLoanInterested = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const userId = sessionStorage.getItem("userId");
    if (!userId) return;

    axios
      .get(`https://be.corebankers.in/pending-loan-interested?userId=${userId}`)
      .then((res) => {
        setLeads(res.data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch pending loan interested leads:", err);
        setLoading(false);
      });
  }, []);

  const handleRowClick = (lead) => {
  const loanType = lead.loan_type?.toUpperCase();
  const validTypes = ["PL", "BL", "AL", "HL", "LAP"];

  if (validTypes.includes(loanType) && lead.random_id) {
    navigate(`/process-flow/${loanType.toLowerCase()}/${lead.random_id}`, {
      state: { lead },
    });
  } else {
    console.warn("Missing loan type or random_id:", lead);
  }
};

  if (loading) return <CircularProgress sx={{ m: 4 }} />;

  const summary = getSummary(leads);

  return (
    <Box p={3}>
      <LoanSummary summary={summary} />

      <Paper elevation={3} className="pli-container">
        <TableContainer>
          <Table size="small">
            <TableHead className="pli-header">
              <TableRow>
                <TableCell align="center" className="pli-header-cell">Lead ID</TableCell>
                <TableCell align="center" className="pli-header-cell">
                  Name
                </TableCell>
                <TableCell align="center" className="pli-header-cell">
                  Mobile Number
                </TableCell>
                <TableCell align="center" className="pli-header-cell">
                  Employment Type
                </TableCell>
                <TableCell align="center" className="pli-header-cell">
                  Loan Type
                </TableCell>
                <TableCell align="center" className="pli-header-cell">
                  Req Loan Amt
                </TableCell>
                <TableCell align="center" className="pli-header-cell">
                  Monthly Income
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.map((lead, idx) => (
                <TableRow
                  key={idx}
                  className={idx % 2 === 0 ? "row-even" : "row-odd"}
                  onClick={() => handleRowClick(lead)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell align="center">{lead.id}</TableCell>
                  <TableCell align="left">{lead.name || ""}</TableCell>
                  <TableCell align="center">
                    {lead.mobile_number || ""}
                  </TableCell>
                  <TableCell align="center">{lead.employment || ""}</TableCell>
                  <TableCell align="center">{lead.loan_type || ""}</TableCell>
                  <TableCell align="center">
                    {formatAmount(lead.req_loan_amt)}
                  </TableCell>
                  <TableCell align="center">
                    {formatAmount(lead.monthly_income)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default PendingLoanInterested;